import { CONTACT, LEGAL_ENTITY, SITE } from '@/constants/site.constants';
import type { LegalDocumentContent } from '@/types/legal.types';

/**
 * Privacy policy covering the data the Shoutt app and server actually handle —
 * see server/src/models for the source of truth on stored fields. Bracketed
 * values come from site.constants.ts and must be filled in before launch.
 */
export const PRIVACY_CONTENT: LegalDocumentContent = {
  title: 'Privacy Policy',
  summary: `How ${SITE.name} collects, uses, shares and protects your personal data when you use our app and this website.`,
  sections: [
    {
      id: 'scope',
      heading: '1. Who this applies to',
      paragraphs: [
        `This policy covers personal data processed by ${LEGAL_ENTITY.name}, ${LEGAL_ENTITY.address} ("Shoutt", "we", "us") when you use the ${SITE.name} mobile app or this website.`,
        'We are the data fiduciary for that data under the Digital Personal Data Protection Act, 2023 and applicable Indian rules.',
      ],
    },
    {
      id: 'what-we-collect',
      heading: '2. What we collect',
      paragraphs: ['Information you give us:'],
      bullets: [
        'Account details: your name, college email address, and the campus derived from that email domain.',
        'Profile details: department, year, stream, area you stay in, mobile number, profile photo and bio.',
        'Verification: a student ID card image, if you submit one for verification.',
        'Listings and Shoutts: item titles, descriptions, categories, prices, availability and photos you upload.',
        'Bookings: rental dates, deposit and price details, condition notes and photos at pickup and return, and damage reports.',
        'Payments: your UPI ID or bank details for payouts, the payment reference you enter, and any payment-proof screenshot you upload. We never see or store your card number, UPI PIN or banking password.',
        'Messages: chat messages you send inside a booking, including images and any location you choose to share.',
        'Support and reports: contact-form messages, support tickets, and reports you file about other users or content.',
      ],
    },
    {
      id: 'automatic',
      heading: '3. What we collect automatically',
      bullets: [
        'Device and session data: device model and operating system, app version, IP address, and browser or app user-agent, kept with your login session.',
        'Push tokens: a device notification token so we can send you booking and message alerts.',
        'Usage data: which screens and features you use, coins earned and spent, and activity that feeds your Trust Score.',
        'Diagnostics: crash reports and error traces, so we can fix what breaks.',
      ],
    },
    {
      id: 'location',
      heading: '4. Location',
      paragraphs: [
        'Shoutt does not track your location in the background. Location is used only when you choose to share it in a booking chat, for example while walking to a handover. Live location sharing expires automatically, and you can stop it at any time.',
      ],
    },
    {
      id: 'how-we-use',
      heading: '5. How we use your data',
      bullets: [
        'To create and verify your account and place you on the right campus.',
        'To show your listings and Shoutts to other verified students on your campus, and to run bookings, chat and handovers.',
        'To process payments, hold and release security deposits, calculate fees and make payouts.',
        'To calculate your Trust Score, coins and reviews.',
        'To keep the platform safe: screening content, investigating reports, resolving damage disputes, and detecting fraud or abuse.',
        'To send you service notifications such as booking updates, messages, reminders and, where you have not opted out, occasional product announcements.',
        'To fix bugs, measure how features are used, and improve the product.',
        'To comply with legal obligations and to establish or defend legal claims.',
      ],
    },
    {
      id: 'legal-basis',
      heading: '6. Our legal basis',
      paragraphs: [
        'We process your data with your consent, given when you create an account and accept this policy, and for the legitimate uses recognised under the Digital Personal Data Protection Act, 2023, including providing the service you asked for, preventing fraud, and meeting legal obligations.',
        'You can withdraw consent at any time by deleting your account. Withdrawing consent does not affect processing already carried out, or data we must retain by law.',
      ],
    },
    {
      id: 'who-sees',
      heading: '7. Who can see your data',
      bullets: [
        'Other students on your campus see your name, profile photo, department and year, bio, Trust Score, ratings, and your listings and Shoutts. They do not see your email address, mobile number or ID document.',
        'The other party to a booking additionally sees the chat, and anything you choose to share in it.',
        'Our team can access account, booking, payment and report data where needed to verify payments, review disputes and moderate content.',
      ],
    },
    {
      id: 'processors',
      heading: '8. Service providers we use',
      paragraphs: [
        'We share the minimum necessary data with providers who help us run Shoutt. Each is bound to use it only on our instructions:',
      ],
      bullets: [
        'Google Firebase: file and image storage, push notification delivery, and crash reporting.',
        'MongoDB Atlas: hosting of our application database.',
        'SendGrid: sending transactional email such as OTPs and booking notices.',
        'Sentry: application error monitoring and diagnostics.',
        'Anthropic (Claude): automated content-safety screening of listings, Shoutts and their images, and rental price suggestions. Content sent for screening is not used to train the provider’s models.',
      ],
    },
    {
      id: 'automated',
      heading: '9. Automated screening',
      paragraphs: [
        'Listings, Shoutts and their images are screened automatically for illegal, unsafe, sexual, hateful or fraudulent content, and suggested rental prices are generated automatically from the item details you enter.',
        'Automated screening flags content for review; it does not permanently ban an account on its own. A person on our team reviews flagged content and reports before any account action is taken, and you can contest an action by writing to us.',
      ],
    },
    {
      id: 'legal-disclosure',
      heading: '10. Legal disclosure',
      paragraphs: [
        'We may disclose data to law enforcement, regulators or courts where we are legally required to, or where it is necessary to investigate fraud, protect someone’s safety, or defend our legal rights.',
        'If Shoutt is involved in a merger, acquisition or transfer of assets, your data may be transferred as part of that transaction; we will notify you if that happens.',
      ],
    },
    {
      id: 'retention',
      heading: '11. How long we keep it',
      bullets: [
        'Account and profile data: while your account is open, and for a reasonable period afterwards.',
        'Booking, payment and deposit records: retained after the rental completes for as long as required for accounting, tax and dispute purposes.',
        'Chat messages: retained for the life of the conversation so both sides have a record of what was agreed.',
        'Verification documents: kept only as long as needed to verify your account.',
        'Diagnostics and logs: kept for a short window, then deleted or aggregated.',
      ],
    },
    {
      id: 'security',
      heading: '12. How we protect it',
      bullets: [
        'Data is encrypted in transit, and access to production data is restricted to authorised team members.',
        'Sensitive values such as one-time passwords are redacted from our logs.',
        'Sessions are token-based and can be revoked; log in only on devices you control.',
        'No system is perfectly secure. If a breach affects your personal data, we will notify you and the Data Protection Board as required.',
      ],
    },
    {
      id: 'your-rights',
      heading: '13. Your rights',
      bullets: [
        'Access: ask for a summary of the personal data we hold about you and how it is processed.',
        'Correction: fix inaccurate or incomplete data, most of which you can edit directly in the app.',
        'Erasure: ask us to delete your account and data, subject to records we must keep by law.',
        'Withdraw consent: stop further processing by deleting your account.',
        'Nomination: nominate someone to exercise your rights if you are unable to.',
        'Grievance redressal: raise a complaint with our Grievance Officer, and escalate to the Data Protection Board of India if you are unsatisfied.',
      ],
      paragraphs: [`To exercise any of these, write to ${CONTACT.email} from your registered college email address.`],
    },
    {
      id: 'children',
      heading: '14. Children',
      paragraphs: [
        'Shoutt is intended for college students aged 18 and over. We do not knowingly collect data from children. If you believe a child has created an account, contact us and we will remove it.',
      ],
    },
    {
      id: 'transfers',
      heading: '15. Where your data is processed',
      paragraphs: [
        'Our providers may process data on servers outside India. Where that happens, we rely on contractual protections and comply with any restrictions notified by the Government of India.',
      ],
    },
    {
      id: 'changes',
      heading: '16. Changes to this policy',
      paragraphs: [
        'We will update this page when our practices change, and update the "Last updated" date. For material changes we will also notify you in the app.',
      ],
    },
    {
      id: 'contact',
      heading: '17. Contact us',
      paragraphs: [
        `General privacy questions: ${CONTACT.email}.`,
        `Grievance Officer: ${LEGAL_ENTITY.grievanceOfficer}, ${LEGAL_ENTITY.name}, ${LEGAL_ENTITY.address}. Email: ${CONTACT.grievanceEmail}. We acknowledge grievances within 24 hours and aim to resolve them within 15 days.`,
      ],
    },
  ],
};
